/**
 * Demand API client (Phase 3c Step 8).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 */

import type {
  DemandBatch,
  DemandBatchCreateInput,
  DemandBatchDetail,
  DemandLine,
  DemandLineCreateInput,
  DemandLinePatchInput,
} from './types'

export type DemandApiError = {
  ok: false
  error: {
    code: string
    message: string
    status: number
    details?: Record<string, unknown>
  }
}

export class DemandApiClientError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, unknown>

  constructor(
    code: string,
    message: string,
    status: number,
    details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'DemandApiClientError'
    this.code = code
    this.status = status
    this.details = details
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as DemandApiError
  throw new DemandApiClientError(
    err?.error?.code ?? 'HTTP_ERROR',
    err?.error?.message ?? `${fallback} (${res.status})`,
    err?.error?.status ?? res.status,
    err?.error?.details,
  )
}

export async function listBatchesApi(): Promise<DemandBatch[]> {
  const res = await fetch('/api/demand-batches')
  const body = await parseJson<
    { ok: true; batches: DemandBatch[] } | DemandApiError
  >(res)
  throwIfApiError(res, body, 'Failed to list demand batches')
  return (body as { batches: DemandBatch[] }).batches
}

export async function getBatchApi(
  id: string,
): Promise<DemandBatchDetail | null> {
  const res = await fetch(`/api/demand-batches/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; batch: DemandBatchDetail } | DemandApiError
  >(res)
  throwIfApiError(res, body, 'Failed to get demand batch')
  return (body as { batch: DemandBatchDetail }).batch
}

export async function createBatchApi(
  input: DemandBatchCreateInput,
): Promise<DemandBatch> {
  const res = await fetch('/api/demand-batches', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<
    { ok: true; batch: DemandBatch } | DemandApiError
  >(res)
  throwIfApiError(res, body, 'Tạo đợt nhu cầu thất bại')
  return (body as { batch: DemandBatch }).batch
}

export async function createLineApi(
  batchId: string,
  input: DemandLineCreateInput,
): Promise<DemandLine> {
  const res = await fetch(
    `/api/demand-batches/${encodeURIComponent(batchId)}/lines`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<{ ok: true; line: DemandLine } | DemandApiError>(
    res,
  )
  throwIfApiError(res, body, 'Thêm dòng nhu cầu thất bại')
  return (body as { line: DemandLine }).line
}

export async function updateLineApi(
  batchId: string,
  lineId: string,
  patch: DemandLinePatchInput,
): Promise<DemandLine> {
  const res = await fetch(
    `/api/demand-batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  const body = await parseJson<{ ok: true; line: DemandLine } | DemandApiError>(
    res,
  )
  throwIfApiError(res, body, 'Sửa dòng nhu cầu thất bại')
  return (body as { line: DemandLine }).line
}

export async function deleteLineApi(
  batchId: string,
  lineId: string,
): Promise<void> {
  const res = await fetch(
    `/api/demand-batches/${encodeURIComponent(batchId)}/lines/${encodeURIComponent(lineId)}`,
    { method: 'DELETE' },
  )
  const body = await parseJson<{ ok: true } | DemandApiError>(res)
  throwIfApiError(res, body, 'Xóa dòng nhu cầu thất bại')
}

export interface PushSalesOrdersToDemandInput {
  target:
    | { mode: 'create_new'; title: string }
    | { mode: 'existing'; batch_id: string }
  lines: Array<{
    sales_order_line_id: string
    quantity?: number
  }>
}

export interface PushSalesOrdersToDemandResult {
  ok: true
  batch_id: string
  created_lines: DemandLine[]
}

export async function pushSalesOrdersToDemandApi(
  input: PushSalesOrdersToDemandInput,
): Promise<PushSalesOrdersToDemandResult> {
  const res = await fetch('/api/demand-batches/from-sales-orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<PushSalesOrdersToDemandResult | DemandApiError>(res)
  throwIfApiError(res, body, 'Không thể đưa đơn hàng vào nhu cầu')
  return body as PushSalesOrdersToDemandResult
}
